"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFragmentTracker = exports.FragmentsTracker = void 0;
const react_1 = __importStar(require("react"));
const FragmentsTrackerContext = (0, react_1.createContext)(undefined);
/**
 * Component that helps with tracking events with fragments of data that is not colocated in the same component. Use in combination with `useFragmentTracker`. Refers here for usage {@link https://github.com/cultureamp/node-packages/tree/master/packages/amplitude#track-events-with-fragments-of-data-not-colocated-in-the-same-component}
 * @param event - The Avo function that you want to track
 * @param {string[]} fragmentNames - The list of fragment names of data that is not colocated in your component which you want to track with useFragmentTracker
 * @param children - Children components
 * @param sharedEventProperties - Event properties that you have access to in the current component
 */
const FragmentsTracker = ({ event, fragmentNames: fragments, children, sharedEventProperties, }) => {
    const parentContext = (0, react_1.useContext)(FragmentsTrackerContext);
    const [trackerState, dispatch] = useTrackerStateReducer(fragments);
    const [contextValue] = (0, react_1.useState)({
        parentContext,
        event,
        fragments,
        lookUpForContext: (eventFromChild, fragment, fragmentData) => {
            const foundContext = event === eventFromChild;
            if (foundContext) {
                dispatch({ fragment, fragmentData });
            }
            else {
                if (parentContext) {
                    parentContext.lookUpForContext(eventFromChild, fragment, fragmentData);
                }
                else {
                    // eslint-disable-next-line no-console
                    console.error("FragmentsTracker cannot find matching context provider for", eventFromChild.name);
                }
            }
        },
    });
    useSendEventWhenReady(event, trackerState, sharedEventProperties);
    return (react_1.default.createElement(FragmentsTrackerContext.Provider, { value: contextValue }, children));
};
exports.FragmentsTracker = FragmentsTracker;
const useTrackerStateReducer = (fragments) => {
    const reducer = (trackerState, { fragment, fragmentData }) => {
        const isValidFragment = fragments.filter(item => item === fragment).length > 0;
        if (isValidFragment) {
            return Object.assign(Object.assign({}, trackerState), { [fragment]: {
                    current: fragmentData,
                    reportsCount: trackerState[fragment].reportsCount + 1,
                } });
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`FragmentsTracker only tracks these fragments ${fragments} and ${fragment} provided by useFragmentTracker is not valid`);
            return trackerState;
        }
    };
    const initialState = fragments.reduce((acc, fragment) => (Object.assign(Object.assign({}, acc), { [fragment]: {
            current: {},
            reportsCount: 0,
        } })), {});
    return (0, react_1.useReducer)(reducer, initialState);
};
const useSendEventWhenReady = (event, trackerState, additionalEventProperties) => {
    const wasReported = (0, react_1.useRef)(false);
    (0, react_1.useEffect)(() => {
        const fragments = Object.values(trackerState);
        if (wasReported.current ||
            fragments.some(fragment => fragment.reportsCount === 0)) {
            return;
        }
        const eventProperties = fragments.reduce((acc, { current }) => (Object.assign(Object.assign({}, acc), current)), Object.assign({}, additionalEventProperties));
        wasReported.current = true;
        event(eventProperties);
    }, [trackerState, event, additionalEventProperties]);
};
/**
 * Hook that helps reporting fragment of data to the correct `FragmentsTracker`. Use in combination with `FragmentsTracker`. Refers here for usage {@link https://github.com/cultureamp/node-packages/tree/master/packages/amplitude#track-events-with-fragments-of-data-not-colocated-in-the-same-component}
 * @param {string} fragment - The fragment that you are tracking. This needs to match with `fragmentNames` provided to your `FragmentsTracker`. `FragmentsTracker` will only send event once all of `fragmentNames` data are reported
 * @param event - The Avo function that you want to track. Makes sure that this is the same as `event` provided to your `FragmentsTracker`
 */
const useFragmentTracker = (fragment, event) => {
    const context = (0, react_1.useContext)(FragmentsTrackerContext);
    return (0, react_1.useCallback)((fragmentData) => {
        if (context) {
            context.lookUpForContext(event, fragment, fragmentData);
        }
        else {
            // eslint-disable-next-line no-console
            console.error(`FragmentsTracker has not been set up for fragment: ${fragment} of ${event.name}`);
        }
    }, [fragment, event, context]);
};
exports.useFragmentTracker = useFragmentTracker;
//# sourceMappingURL=FragmentsTracker.js.map